import { api } from './client';
import type { Page } from './catalogs';

// ==================== Tax Declaration Articles ====================

export type TaxFormType = 'VAT' | 'PROFIT';
export type DeclarationType = 'IMPORT' | 'EXPORT';

export interface TaxDeclarationArticle {
  id: string;
  code: string;
  form_type: TaxFormType;
  line_number: string;
  name: string;
  description: string | null;
  is_active: boolean;
  effective_from: string;
  effective_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxDeclarationArticleCreate {
  code: string;
  form_type: TaxFormType;
  line_number: string;
  name: string;
  description?: string | null;
  is_active?: boolean;
  effective_from: string;
  effective_until?: string | null;
}

const TDA_BASE = '/ref/tax-declaration-articles';

export const taxDeclarationArticles = {
  list: (params?: {
    form_type?: TaxFormType;
    is_active?: boolean;
    limit?: number;
  }) =>
    api.get<Page<TaxDeclarationArticle>>(TDA_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<TaxDeclarationArticle>(`${TDA_BASE}/${id}`).then((r) => r.data),
  create: (payload: TaxDeclarationArticleCreate) =>
    api.post<TaxDeclarationArticle>(TDA_BASE, payload).then((r) => r.data),
  update: (id: string, payload: Partial<TaxDeclarationArticleCreate>) =>
    api.patch<TaxDeclarationArticle>(`${TDA_BASE}/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${TDA_BASE}/${id}`).then(() => undefined),
};

// ==================== Customs Declaration Numbers Register ====================

export interface CustomsRegisterEntry {
  id: string;
  document_number: string;
  customs_number: string;
  customs_date: string;
  customs_office_name: string | null;
  country_origin_code: string | null;
  declaration_type: DeclarationType;
  total_customs_value: string;
  total_duty: string;
  total_vat: string;
  posted: boolean;
  posted_at: string | null;
  receipts_count: number;
  tax_invoices_count: number;
  items_count: number;
}

const REG_BASE = '/doc/customs-declaration-numbers-register';

export const customsNumbersRegister = {
  list: (params?: {
    organization_id?: string;
    declaration_type?: DeclarationType;
    country_origin_code?: string;
    posted?: boolean;
    customs_number_ilike?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
  }) =>
    api.get<Page<CustomsRegisterEntry>>(REG_BASE, { params }).then((r) => r.data),
};

// ==================== Customs Auto-fill Helper ====================

export interface CustomsAutoFillResponse {
  customs_number: string;
  customs_date: string;
  country_origin_code: string | null;
  customs_office_name: string | null;
  total_customs_value: string;
  total_duty: string;
  total_vat: string;
  items_count: number;
  uktzed_codes: string[];
}

export const customsHelper = {
  autoFill: (customsDeclarationId: string) =>
    api
      .get<CustomsAutoFillResponse>(
        `/tax/tax-invoices-incoming/helpers/customs-declaration/${customsDeclarationId}`,
      )
      .then((r) => r.data),
};
